import { ResourceCode } from '../../constants/resourceCodes';

/** RAW=具体日期/日期范围列；WOW=环比列；AVG=整个可见时间范围的单值聚合列（日均/周均/月均/合计等，语义是"非具体日期"而非严格意义的算术平均） */
export type DashboardAnnotationColumnType = 'RAW' | 'WOW' | 'AVG';

export interface CellContext {
    businessType?: string;
    customerType?: string;
    hubId?: string;
    metricKey: string;
    columnType: DashboardAnnotationColumnType;
    dateKey: string;
}

export interface PageAnnotationConfig {
    pageKey: string;
    /** 该看板的写入资源码：持有即可新增注释 */
    writeResource: string;
    /** 该看板的管理资源码：持有即可编辑/删除任意人的注释 */
    adminResource: string;
    /** 从 CellContext 提取 match_dimensions_json 的维度字段（businessType 等差异化字段） */
    buildCellDimensions: (ctx: CellContext) => Record<string, string>;
}

export const PAGE_KEY_SORTING = 'dashboard.sorting';
export const PAGE_KEY_SCALE = 'dashboard.scale';
export const PAGE_KEY_NO_UPDATE = 'dashboard.no_update';
export const PAGE_KEY_DELIVERY = 'dashboard.delivery';

const sortingPageAnnotationConfig: PageAnnotationConfig = {
    pageKey: PAGE_KEY_SORTING,
    writeResource: ResourceCode.dashboard.SORTING_ANNOTATION_WRITE,
    adminResource: ResourceCode.dashboard.SORTING_ANNOTATION_ADMIN,
    buildCellDimensions: (ctx) => ({ businessType: ctx.businessType ?? '' }),
};

/**
 * 规模看板：行结构为 Nation→客户类型→客户名→Hub 的多层聚合，行身份（含客户类型/客户名/hub）
 * 已完整编码进 record.key（如 hourly_cn_1_TikTok CBT_hub_5），故直接复用 key 作 metricKey，
 * 不再拆分 customerType/customerName 为独立签名字段（拆分是重复信息，无额外区分力）。
 * 没有 WarehouseBusinessType 概念，businessType 字段复用为 scaleTab/orderSubTab/订单类型的组合 tableId。
 */
const scalePageAnnotationConfig: PageAnnotationConfig = {
    pageKey: PAGE_KEY_SCALE,
    writeResource: ResourceCode.dashboard.SCALE_ANNOTATION_WRITE,
    adminResource: ResourceCode.dashboard.SCALE_ANNOTATION_ADMIN,
    buildCellDimensions: (ctx) => ({ businessType: ctx.businessType ?? '' }),
};

/**
 * 断更看板：行结构为 Hub→指标树（含按责任 hub/whs 动态拆分的 L3/L4 行），行身份（含 hub、
 * 指标层级、动态拆分维度）已完整编码进 row.key（如 5-2-linehaulInboundHub-8），故直接复用
 * key 作 metricKey，不再拆分 hubCode 等字段（拆分是重复信息，无额外区分力，与规模看板同理）。
 * 没有 tab/businessType 概念（单一页面结构，无客户筛选），businessType 字段留空、不展示客户 context。
 */
const noUpdatePageAnnotationConfig: PageAnnotationConfig = {
    pageKey: PAGE_KEY_NO_UPDATE,
    writeResource: ResourceCode.dashboard.QUALITY_ANNOTATION_WRITE,
    adminResource: ResourceCode.dashboard.QUALITY_ANNOTATION_ADMIN,
    buildCellDimensions: (ctx) => ({ businessType: ctx.businessType ?? '' }),
};

/**
 * 尾程看板：行结构为 全美→Hub→Warehouse 的地理层级聚合，列结构才是指标（到货包裹数/领取率/…），
 * 与前 3 个看板"行=指标"相反。行身份（含 hub/warehouse）已完整编码进 record.key（如 hub_5、
 * warehouse_5_12），故复用 key 作 businessType（没有 WarehouseBusinessType 概念，字段复用同规模看板）；
 * metricKey 在此改为标识"当前是哪一列指标"（如 arrivedCount、pickupRate），与前 3 个看板
 * "metricKey=行"相反——字段语义仍是"该 cell 对应哪个 metric"，只是这里由列而非行决定。
 * 分拣仓筛选下拉只收窄可见行集合，不改变任一行自身的身份/取值（同客户筛选先例），故不入签名。
 */
const deliveryPageAnnotationConfig: PageAnnotationConfig = {
    pageKey: PAGE_KEY_DELIVERY,
    writeResource: ResourceCode.dashboard.DELIVERY_ANNOTATION_WRITE,
    adminResource: ResourceCode.dashboard.DELIVERY_ANNOTATION_ADMIN,
    buildCellDimensions: (ctx) => ({ businessType: ctx.businessType ?? '' }),
};

/** page_key → 该页签名字段/展示标签配置；新增看板页面只需在此加一条，后端零改动 */
export const dashboardAnnotationRegistry: Record<string, PageAnnotationConfig> = {
    [PAGE_KEY_SORTING]: sortingPageAnnotationConfig,
    [PAGE_KEY_SCALE]: scalePageAnnotationConfig,
    [PAGE_KEY_NO_UPDATE]: noUpdatePageAnnotationConfig,
    [PAGE_KEY_DELIVERY]: deliveryPageAnnotationConfig,
};
